import { useKeycloak } from '@react-keycloak/web';
import { useMemo } from 'react';

import dayjs from 'dayjs';

import { Services } from '@/api/Services';
import { HourlyWeather } from '@/api/data-contracts';
import {
  HourlyWeatherData,
  MeteoWarningLevel,
  NightSection,
  getSectionWarnings,
} from '@/features/service-tasks/model/meteo';

export type WeatherSeverity = 'BLUE' | 'ORANGE' | 'RED';

export type WeatherSkipReason =
  | 'NOT_APPLICABLE_SERVICE_TYPE'
  | 'NOT_OUTDOOR_PLACEMENT'
  | 'TIMELINE_NOT_SET'
  | 'LOCATION_COORDINATES_NOT_SET'
  | 'LOCATION_COORDINATES_INVALID'
  | 'OUTSIDE_FORECAST_WINDOW'
  | 'FORECAST_UNAVAILABLE';

export interface TaskWeatherData {
  badge: WeatherSeverity | null;
  reasonNotCalculated: WeatherSkipReason | null;
  todaySection: NightSection | null;
}

export const badgeToWarningLevel = (badge: WeatherSeverity | null): MeteoWarningLevel => {
  if (badge === 'RED') return 'red';
  if (badge === 'ORANGE') return 'orange';
  return 'none';
};

const toHourlyWeatherData = (
  h: HourlyWeather,
  adverseSeverity: MeteoWarningLevel,
): HourlyWeatherData => ({
  time: h.time,
  hour: dayjs(h.time).format('HH:00'),
  temperature: Math.round(h.temperature ?? 0),
  windSpeed: Math.round(h.windSpeed * 10) / 10,
  windGusts: Math.round(h.windGusts * 10) / 10,
  precipitation: Math.round(h.precipitation * 10) / 10,
  adverseSeverity: h.adverse ? adverseSeverity : 'none',
});

const resultToWeatherData = (result: {
  badge?: string;
  reasonNotCalculated?: string;
  hourly: HourlyWeather[];
}): TaskWeatherData => {
  const badge = (result.badge as WeatherSeverity | undefined) ?? null;
  const reasonNotCalculated = (result.reasonNotCalculated as WeatherSkipReason | undefined) ?? null;
  const adverseSeverity = badgeToWarningLevel(badge);
  const hours = result.hourly.map((h) => toHourlyWeatherData(h, adverseSeverity));

  const todaySection: NightSection | null =
    hours.length > 0
      ? {
          date: dayjs(hours[0].time).startOf('day'),
          hours,
          warnings: getSectionWarnings(hours),
        }
      : null;

  return { badge, reasonNotCalculated, todaySection };
};

export const fetchTaskWeather = async (
  services: Services,
  taskId: string,
  signal?: AbortSignal,
): Promise<TaskWeatherData> => {
  const { data } = await services.getServiceTasksWeather({ taskIds: [taskId] }, { signal });

  const result = data.results[0];
  if (!result) throw new Error('No weather result returned');

  return resultToWeatherData(result);
};

export const fetchBatchWeather = async (
  services: Services,
  taskIds: string[],
  signal?: AbortSignal,
): Promise<Map<string, TaskWeatherData>> => {
  const { data } = await services.getServiceTasksWeather({ taskIds }, { signal });

  const map = new Map<string, TaskWeatherData>();
  data.results.forEach((result) => {
    map.set(result.taskId, resultToWeatherData(result));
  });
  return map;
};

export const useWeatherService = () => {
  const { keycloak } = useKeycloak();
  const servicesApi = useMemo(
    () => new Services({ headers: { Authorization: `Bearer ${keycloak.token}` } }),
    [keycloak.token],
  );
  return {
    getTaskWeather: (taskId: string, signal?: AbortSignal) =>
      fetchTaskWeather(servicesApi, taskId, signal),
    getTasksWeather: (taskIds: string[], signal?: AbortSignal) =>
      fetchBatchWeather(servicesApi, taskIds, signal),
  };
};
